import { memo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { StampIcon } from '@/components/ui/stamp-icon'
import { useAuth } from '@/lib/auth-context'

type AuthMode = 'login' | 'signup'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

function GitHubIcon(): React.JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12Z" />
    </svg>
  )
}

/** Left panel is memoized so it never re-renders when form state changes */
const LeftPanel = memo(function LeftPanel() {
  return (
    <div className="auth-panel-left relative flex flex-col justify-center overflow-hidden bg-[#1a1216] px-[60px] py-[60px]">
      {/* Orange glow blobs */}
      <div className="pointer-events-none absolute -top-[100px] -right-[80px] h-[400px] w-[400px] rounded-full bg-[#FF6B00] opacity-[0.08] blur-[60px]" />
      <div className="pointer-events-none absolute -bottom-[60px] -left-[40px] h-[300px] w-[300px] rounded-full bg-[#FF9E4F] opacity-[0.06] blur-[50px]" />

      {/* Floating envelopes */}
      <div
        className="absolute top-[12%] right-[20%]"
        style={{ animation: 'oe-envelope-float 6s ease-in-out 0s infinite' }}
      >
        <svg width="48" height="36" viewBox="0 0 48 36" fill="none" aria-hidden="true">
          <rect
            x="1"
            y="1"
            width="46"
            height="34"
            rx="6"
            fill="white"
            stroke="#FF6B00"
            strokeWidth="1.5"
            opacity="0.5"
          />
          <path d="M1 5L24 20L47 5" stroke="#FF6B00" strokeWidth="1.5" opacity="0.3" />
        </svg>
      </div>
      <div
        className="absolute bottom-[8%] left-[10%]"
        style={{ animation: 'oe-envelope-float 6s ease-in-out 2s infinite' }}
      >
        <svg width="48" height="36" viewBox="0 0 48 36" fill="none" aria-hidden="true">
          <rect
            x="1"
            y="1"
            width="46"
            height="34"
            rx="6"
            fill="white"
            stroke="#FF6B00"
            strokeWidth="1.5"
            opacity="0.5"
          />
          <path d="M1 5L24 20L47 5" stroke="#FF6B00" strokeWidth="1.5" opacity="0.3" />
        </svg>
      </div>
      <div
        className="absolute top-[45%] right-[5%]"
        style={{ animation: 'oe-envelope-float 6s ease-in-out 4s infinite' }}
      >
        <svg width="48" height="36" viewBox="0 0 48 36" fill="none" aria-hidden="true">
          <rect
            x="1"
            y="1"
            width="46"
            height="34"
            rx="6"
            fill="white"
            stroke="#FF6B00"
            strokeWidth="1.5"
            opacity="0.5"
          />
          <path d="M1 5L24 20L47 5" stroke="#FF6B00" strokeWidth="1.5" opacity="0.3" />
        </svg>
      </div>

      <div className="relative z-10 max-w-[420px]">
        <div className="mb-[60px] inline-flex items-center gap-2.5">
          <StampIcon size={40} variant="solid" />
          <span className="text-[22px] font-extrabold tracking-tight text-white">one-email</span>
        </div>
        <h1 className="mb-5 text-[44px] leading-[1.1] font-extrabold tracking-tight text-white">
          Your emails, your{' '}
          <span
            className="italic text-[#FF6B00]"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}
          >
            dashboard.
          </span>
        </h1>
        <p className="mb-10 text-[17px] leading-relaxed text-[#a09898]">
          Monitor deliverability, manage domains, and track every email.
        </p>
        <div className="flex gap-8">
          {[
            { value: 'Free', label: 'Free tier' },
            { value: '10K', label: 'Emails included' },
            { value: '<2s', label: 'To inbox' },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="font-mono text-2xl font-extrabold tracking-tight text-white">
                {stat.value}
              </div>
              <div className="mt-0.5 text-xs font-medium text-[#a09898]">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
})

export function LoginPage(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login'
  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { login, signup } = useAuth()
  const navigate = useNavigate()

  function switchMode(newMode: AuthMode) {
    setMode(newMode)
    setError('')
  }

  function handleOAuthRedirect(provider: 'github') {
    const callbackURL = encodeURIComponent(`${window.location.origin}/`)
    window.location.href = `${API_BASE_URL}/auth/sign-in/social?provider=${provider}&callbackURL=${callbackURL}`
  }

  async function handlePasswordLogin(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      await login(email, password)
      navigate('/')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handlePasswordSignup(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      await signup(name, email, password)
      navigate('/')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Signup failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isLogin = mode === 'login'

  return (
    <div className="auth-layout bg-gradient-to-b from-[#FDF8F3] to-[#FFF0E0] font-sans text-[#1a1216]">
      <LeftPanel />

      {/* Right panel -- auth form */}
      <div className="auth-panel-right relative flex flex-col px-14 pt-[80px] pb-[60px] overflow-y-auto">
        <div className="pointer-events-none absolute -top-[60px] -right-[60px] h-[200px] w-[200px] rounded-full bg-gradient-to-br from-[#FFE4CC] to-[#FFDAB3] opacity-40 blur-[50px]" />

        <div className="relative z-10 mx-auto w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="auth-mobile-logo mb-8 items-center gap-2.5">
            <StampIcon size={36} variant="cream" />
            <span className="text-xl font-extrabold tracking-tight text-[#1a1216]">one-email</span>
          </div>

          {/* Mode toggle */}
          <div
            role="tablist"
            className="mb-10 flex overflow-hidden rounded-2xl border border-[#EDE9E4] bg-white"
          >
            <button
              type="button"
              role="tab"
              aria-selected={!isLogin}
              onClick={() => switchMode('signup')}
              className={`flex-1 cursor-pointer border-none py-3.5 text-[14px] font-bold transition-all ${
                !isLogin ? 'bg-[#FF6B00] text-white' : 'bg-white text-[#999] hover:text-[#1a1216]'
              }`}
            >
              Sign up
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isLogin}
              onClick={() => switchMode('login')}
              className={`flex-1 cursor-pointer border-none py-3.5 text-[14px] font-bold transition-all ${
                isLogin ? 'bg-[#FF6B00] text-white' : 'bg-white text-[#999] hover:text-[#1a1216]'
              }`}
            >
              Log in
            </button>
          </div>

          {/* Heading */}
          <h2 className="mb-2 text-[28px] font-extrabold tracking-tight">
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="mb-8 text-[15px] text-[#6d6462]">
            {isLogin
              ? 'Sign in with your email or a social account to continue.'
              : 'Start sending emails in under 5 minutes. Free sandbox included.'}
          </p>

          {/* OAuth buttons */}
          <div className="flex flex-col gap-3 mb-6">
            <button
              type="button"
              onClick={() => handleOAuthRedirect('github')}
              className="flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-xl border-none bg-[#24292f] px-4 py-3.5 text-[15px] font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1b1f23] hover:shadow-md"
            >
              <GitHubIcon />
              Continue with GitHub
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6 flex items-center">
            <div className="flex-1 border-t border-[#EDE9E4]" />
            <span className="px-4 text-[13px] font-medium text-[#999]">or</span>
            <div className="flex-1 border-t border-[#EDE9E4]" />
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {/* Email/password form */}
          <form
            onSubmit={isLogin ? handlePasswordLogin : handlePasswordSignup}
            className="flex flex-col gap-4"
          >
            {!isLogin && (
              <div>
                <label
                  htmlFor="name"
                  className="mb-1.5 block text-[13px] font-semibold text-[#1a1216]"
                >
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full rounded-xl border border-[#E8E4DF] bg-white px-4 py-3.5 text-[15px] text-[#1a1216] outline-none transition-colors focus:border-[#FF6B00]"
                />
              </div>
            )}
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-[13px] font-semibold text-[#1a1216]"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full rounded-xl border border-[#E8E4DF] bg-white px-4 py-3.5 text-[15px] text-[#1a1216] outline-none transition-colors focus:border-[#FF6B00]"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-[13px] font-semibold text-[#1a1216]"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={!isLogin ? 8 : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={!isLogin ? 'At least 8 characters' : 'Enter your password'}
                className="w-full rounded-xl border border-[#E8E4DF] bg-white px-4 py-3.5 text-[15px] text-[#1a1216] outline-none transition-colors focus:border-[#FF6B00]"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full cursor-pointer rounded-xl border-none bg-[#FF6B00] px-4 py-3.5 text-[15px] font-bold text-white shadow-[0_4px_16px_rgba(255,107,0,0.25)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(255,107,0,0.35)] disabled:opacity-50"
            >
              {isSubmitting
                ? isLogin
                  ? 'Signing in...'
                  : 'Creating account...'
                : isLogin
                  ? 'Sign in'
                  : 'Create account'}
            </button>
          </form>

          {/* Terms (signup only) */}
          {!isLogin && (
            <p className="mt-5 text-center text-xs leading-relaxed text-[#999]">
              By signing up, you agree to our{' '}
              <a href="/legal/terms" className="font-semibold text-[#FF6B00] no-underline">
                Terms
              </a>{' '}
              and{' '}
              <a href="/legal/privacy" className="font-semibold text-[#FF6B00] no-underline">
                Privacy Policy
              </a>
              .
            </p>
          )}

          {/* Mode switch */}
          <p className="mt-7 text-center text-[13px] text-[#999]">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => switchMode(isLogin ? 'signup' : 'login')}
              className="cursor-pointer border-none bg-transparent text-[13px] font-bold text-[#FF6B00]"
            >
              {isLogin ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
